import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Mail, Lock, User, LogIn, Chrome } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const r = await axios.post(`${API}${endpoint}`, payload, { withCredentials: true });
      setUser(r.data);
      // New registrations → onboarding; logins → dashboard
      navigate(mode === "register" ? "/onboarding" : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: "#0F172A" }}
      data-testid="auth-page"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate("/")}
            className="text-2xl font-black mb-2 block mx-auto"
            style={{ color: "#FFC82E", fontFamily: "Outfit, sans-serif" }}
          >
            AusVote.
          </button>
          <p className="text-sm" style={{ color: "#94A3B8" }}>
            {mode === "login" ? "Sign in to your account" : "Create your free account"}
          </p>
        </div>

        <div className="border-2 border-black p-6" style={{ backgroundColor: "#1E293B", boxShadow: "6px 6px 0 #FFC82E" }}>
          {/* Google Sign In */}
          <button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 py-2.5 font-bold text-sm border-2 border-black mb-4 hover:-translate-y-0.5 transition-transform"
            style={{ backgroundColor: "#fff", color: "#0F172A", boxShadow: "2px 2px 0 #000" }}
          >
            <Chrome className="w-4 h-4" /> Continue with Google
          </button>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 h-px" style={{ backgroundColor: "#334155" }} />
            <span className="text-xs font-bold" style={{ color: "#475569" }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: "#334155" }} />
          </div>

          {/* Toggle */}
          <div className="flex border-2 border-black mb-4">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-mode-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className="flex-1 py-2 font-bold text-sm transition-colors"
                style={{
                  backgroundColor: mode === m ? "#FFC82E" : "transparent",
                  color: mode === m ? "#000" : "#94A3B8",
                }}
              >
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
                <input
                  type="text"
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  data-testid="auth-name-input"
                  className="w-full border-2 border-black pl-10 pr-3 py-2 text-sm font-medium focus:outline-none"
                  style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
              <input
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="auth-email-input"
                className="w-full border-2 border-black pl-10 pr-3 py-2 text-sm font-medium focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#94A3B8" }} />
              <input
                type="password"
                placeholder="Password (min 6 chars)"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                data-testid="auth-password-input"
                className="w-full border-2 border-black pl-10 pr-3 py-2 text-sm font-medium focus:outline-none"
                style={{ backgroundColor: "#0F172A", color: "#F8FAFC" }}
              />
            </div>
            {error && (
              <p className="text-xs font-bold" style={{ color: "#C04E3F" }} data-testid="auth-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit-btn"
              className="w-full py-2.5 font-black text-sm border-2 border-black hover:-translate-y-0.5 transition-transform disabled:opacity-60"
              style={{ backgroundColor: "#2E5948", color: "#fff", boxShadow: "2px 2px 0 #000" }}
            >
              <LogIn className="inline w-4 h-4 mr-2" />
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>

        <p className="text-center mt-4 text-xs" style={{ color: "#475569" }}>
          <button onClick={() => navigate("/")} className="hover:underline" style={{ color: "#94A3B8" }}>
            Back to AusVote
          </button>
        </p>
      </div>
    </div>
  );
}
